import AppContext from '@/AppContext';
import Loader from '@/components/common/Loader';
import logoSquare from '@/images/youtalky_com_logo_square.png';
import { isValidDomain } from '@/utils/Regex';
import { ExclamationCircleIcon, EyeIcon, EyeOffIcon } from '@heroicons/react/outline';
import Image from 'next/image';
import Link from 'next/link';
import { useContext, useMemo, useState } from 'react';

export default function Register() {
  const [email, setEmail] = useState('');
  const [password1, setPassword1] = useState('');
  const [password2, setPassword2] = useState('');
  const [inputType, setInputType] = useState('password');
  const [passwordError, setPasswordError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isRegistered, setIsRegistered] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  const context = useContext(AppContext);
  const domain = useMemo(() => `https://${email.split('@')[1]}`, [email]);

  const handleChangeInput = (event) => {
    inputType === 'password' ? setInputType('text') : setInputType('password');
  };

  const handleEmail = (event) => {
    setEmail(event.target.value);
    setEmailError('');
  };

  const handlePassword1 = (event) => {
    if (passwordError && password2) {
      setPasswordError('Hasła muszą być identyczne.');
    } else {
      setPasswordError('');
    }

    setPassword1(event.target.value);
  };
  const handlePassword2 = (event) => {
    let pass2 = event.target.value;

    setPassword2(pass2);
    if (pass2 && pass2.length >= password1.length && password1 !== pass2) {
      setPasswordError('Hasła muszą być identyczne.');
    } else {
      setPasswordError('');
    }
  };

  const comparePasswords = () => {
    if (password1 && password2 && password1 !== password2) {
      setPasswordError('Hasła muszą być identyczne.');
    } else {
      setPasswordError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password1 !== password2) {
      comparePasswords();
      return;
    }
    setIsRegistering(true);
    let obj = {
      email: email,
      password: password1,
    };

    let data = new URLSearchParams(Object.entries(obj)).toString();

    let requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: data,
      redirect: 'follow',
    };

    let response = await fetch(
      `${process.env.NEXT_PUBLIC_DJANGO_URL}/api/auth/users/`,
      requestOptions
    );

    if (!response.ok) {
      let json = await response.json();
      if (json.email) {
        setEmailError(json.email[0]);
      }
      if (json.password) {
        setPasswordError(json.password[0]);
      }
    }

    if (response.ok && response.status === 201) {
      setIsRegistered(true);
      context.setNotification({
        title: 'Rejestracja przebiegła pomyślnie!',
        description:
          'Potwierdź konto klikając w link, który znajdziesz w swojej skrzynce mailowej. Sprawdź również folder ze spamem.',
        type: 'success',
        timeout: 10000,
      });
    } else {
      context.setNotification({
        type: 'warning',
      });
    }
    setIsRegistering(false);
  };

  return (
    <>
      <div className="flex min-h-full bg-white">
        <div className="flex flex-1 flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
          <div className="mx-auto w-full max-w-sm lg:w-96">
            <div>
              <Link href="/" shallow>
                <Image className="h-12 w-12" src={logoSquare} alt="Workflow" fill={false} />
              </Link>
              <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                Dołącz do nas rejestrując się na portalu
              </h2>
              {/* <p className="mt-2 text-sm text-gray-600">
                Or{' '}
                <a href="#" className="font-medium text-blue-600 hover:text-blue-500">
                  start your 14-day free trial
                </a>
              </p> */}
            </div>

            <div className="mt-8">
              <div className="mt-6">
                <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Adres Mejlowy
                    </label>
                    <div className="relative mt-1 rounded-md shadow-sm">
                      <input
                        type="email"
                        name="email"
                        id="email"
                        className={
                          emailError
                            ? 'block w-full rounded-md border-red-300 pr-10 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500 sm:text-sm'
                            : 'block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm'
                        }
                        autoComplete="off"
                        placeholder="np: jan@wp.pl"
                        required
                        aria-invalid="true"
                        aria-describedby="email-error"
                        onChange={handleEmail}
                      />
                      {emailError && (
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                          <ExclamationCircleIcon
                            className="h-5 w-5 text-red-500"
                            aria-hidden="true"
                          />
                        </div>
                      )}
                    </div>
                    {emailError && (
                      <p className="mt-2 text-sm capitalize text-red-600" id="email-error">
                        {emailError}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Hasło
                    </label>
                    <div className="relative mt-1 rounded-md shadow-sm">
                      <input
                        id="password"
                        name="password"
                        type={inputType}
                        className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                        value={password1}
                        onChange={handlePassword1}
                        onBlur={comparePasswords}
                        autoComplete="off"
                        required
                      />

                      <div
                        onClick={handleChangeInput}
                        className="pointer-events-auto absolute inset-y-0 right-0 z-10 flex items-center pr-3"
                      >
                        {inputType === 'password' ? (
                          <EyeIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                        ) : (
                          <EyeOffIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Powtórz hasło
                    </label>
                    <div className="relative mt-1 rounded-md shadow-sm">
                      <input
                        name="password"
                        id="password"
                        className={
                          passwordError
                            ? 'block w-full rounded-md border-red-300 pr-10 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500 sm:text-sm'
                            : 'block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm'
                        }
                        autoComplete="off"
                        required
                        type={inputType}
                        onChange={handlePassword2}
                        onBlur={comparePasswords}
                        value={password2}
                        aria-invalid="true"
                        aria-describedby="password-error"
                      />
                      {passwordError && (
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                          <ExclamationCircleIcon
                            className="h-5 w-5 text-red-500"
                            aria-hidden="true"
                          />
                        </div>
                      )}
                    </div>
                    {passwordError && (
                      <p className="mt-2 text-sm capitalize text-red-600" id="email-error">
                        {passwordError}
                      </p>
                    )}
                  </div>

                  <div>
                    <button
                      data-umami-event="Register"
                      disabled={isRegistered}
                      type="submit"
                      className="flex w-full justify-center rounded-md border border-transparent bg-blue-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-25"
                    >
                      {isRegistering && <Loader />}
                      Zarejestruj
                    </button>

                    {isRegistered && isValidDomain(domain) && (
                      <Link
                        target="_blank"
                        href={domain}
                        className="my-1 flex w-full justify-center rounded-md border border-transparent bg-green-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-25"
                      >
                        Przejdź do swojej poczty
                      </Link>
                    )}
                  </div>
                </form>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center">
                {/* <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                  Zapamiętaj mnie
                </label> */}
              </div>
            </div>
            <div>
              <div className="relative mt-6">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-2 text-gray-500">Masz już konto?</span>
                </div>
              </div>
              <div className="mt-2 text-right">
                <Link
                  href="/auth/login"
                  className="text-sm font-medium text-blue-600 hover:text-blue-500"
                >
                  Kliknij tu aby się zalogować
                </Link>
              </div>
            </div>
          </div>
        </div>
        <div className="relative hidden w-0 flex-1 lg:block">
          <Image
            className="absolute inset-0 h-full w-full object-cover"
            src="https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1908&q=80"
            alt=""
            fill
          />
        </div>
      </div>
    </>
  );
}

Register.layout = 'Auth';
